// src/pages/AboutUsPage.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import '../Styles/AboutUsPage.css';

// Import images from src so they can be bundled (do NOT import from public/)
import otobiImg from '../assets/otobi.jpg';
import oduImg from '../assets/george.jpg';
import avatarPlaceholder from '../assets/otobi.jpg';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  bio: string;
  image: string; // imported image module (string)
  expertise: string[];
}

const AboutUsPage: React.FC = () => {
  // NOTE: member.image values are now imported variables (bundled by the build).
  const teamMembers: TeamMember[] = [
    {
      id: 1,
      name: 'Augustine Ogbaji Otobi',
      role: 'Chief AI Researcher & Founder (PhD candidate, University of Calabar)',
      bio:
        'PhD candidate at the University of Calabar specializing in medical AI applications. Developed the core AI models powering our diagnostic platform with over 5 years of research in machine learning for healthcare.',
      image: otobiImg,
      expertise: ['AI Research', 'Machine Learning', 'Medical Diagnostics', 'PhD Candidate'],
    },
    {
      id: 2,
      name: 'Odo George Obase',
      role: 'Full-Stack Developer (Final-year Student)',
      bio:
        'Final-year Computer Science student specializing in web application development. Created the responsive front-end and robust back-end architecture for our platform.',
      image: oduImg,
      expertise: ['React', 'TypeScript', 'Node.js', 'UI/UX Design'],
    }
  ];

  const stats = [
    { value: '10,000+', label: 'Scans Analyzed' },
    { value: '98.3%', label: 'Accuracy Rate' },
    { value: '50+', label: 'Medical Partners' },
    { value: '12', label: 'Countries Served' }
  ];

  return (
    <div className="about-us-container">
      {/* Hero */}
      <section className="about-hero">
        <div className="hero-content">
          <h1>Transforming Medical Diagnostics with AI</h1>
          <p className="hero-sub">
            We combine clinically-validated AI with a secure, user-friendly platform to make diagnostic imaging faster and more accurate.
          </p>

          <div className="hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="hero-stat">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="hero-actions">
            <Link to="/ScanSelectionPage" className="btn primary">Start a Scan</Link>
            <Link to="/contact" className="btn ghost">Contact Us</Link>
          </div>
        </div>

        <div className="hero-visual" aria-hidden>
          <div className="floating-cards">
            <div className="card card-1" />
            <div className="card card-2" />
            <div className="card card-3" />
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="mission-section">
        <div className="mission-inner">
          <h2>Our Mission</h2>
          <p>
            At MedScan AI we believe early, accurate diagnosis saves lives. Our platform equips clinicians with reliable AI insights, preserving clinical workflows and patient privacy.
          </p>

          <div className="mission-values">
            <article className="value">
              <div className="value-icon">✓</div>
              <h3>Accuracy</h3>
              <p>Models validated with multi-site datasets and clinician review.</p>
            </article>

            <article className="value">
              <div className="value-icon">⚕</div>
              <h3>Clinical Focus</h3>
              <p>Built in collaboration with radiologists and medical experts.</p>
            </article>

            <article className="value">
              <div className="value-icon">🔒</div>
              <h3>Security</h3>
              <p>Encrypted storage, audit logs and role-based access control.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="team-section">
        <div className="section-header">
          <h2>Our Team</h2>
          <p>Meet the experts who built the models and the application.</p>
        </div>

        <div className="team-grid">
          {teamMembers.map(member => (
            <div key={member.id} className="team-card">
              <div className="member-image">
                <img
                  src={member.image}
                  alt={member.name}
                  loading="lazy"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    if (target.src !== avatarPlaceholder) {
                      target.src = avatarPlaceholder;
                    }
                  }}
                />
                <div className="image-overlay" />
              </div>

              <div className="member-info">
                <h3 className="member-name">{member.name}</h3>
                <div className="member-role">{member.role}</div>
                <p className="member-bio">{member.bio}</p>

                <div className="member-meta">
                  <div className="expertise">
                    {member.expertise.map((skill, i) => (
                      <span key={i} className="expertise-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Technology and CTA remain unchanged... */}
      <section className="technology-section">
        <div className="section-header">
          <h2>Our Technology</h2>
          <p>Advanced deep learning and secure deployment for clinical use.</p>
        </div>

        <div className="tech-content">
          <div className="tech-left">
            <h3>How it works</h3>
            <p>Upload scans (DICOM, JPG, PNG), models analyze images and return explainable outputs and confidence scores. Reports can be downloaded or shared with medical teams.</p>
            <ul className="tech-list">
              <li>Clinical-grade models validated across sites</li>
              <li>Explainability & confidence scores for every result</li>
              <li>Secure, privacy-first architecture</li>
            </ul>
          </div>

          <div className="tech-visual" aria-hidden>
            <div className="tech-animation">
              <div className="circle circle-1" />
              <div className="circle circle-2" />
              <div className="center-node">AI</div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-inner">
          <h3>Ready to get started?</h3>
          <p>Schedule a demo or run a pilot with your institution.</p>
          <div className="cta-buttons">
            <Link to="/ScanSelectionPage" className="btn primary">Start a Scan</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;
